import { createSlice } from '@reduxjs/toolkit';
import { User } from 'modules/types/User';
import { updateProfileData } from 'modules/profilePage/redux/thunks';
import { fetchUser, forgottenPassword, login, tfaVerifyCode } from './thunks';

export interface LoginState {
  loading: boolean;
  user?: User;
  loginError?: string;
  tfaQrCode?: string;
  tfaError?: string;
  forgottenPasswordError?: string;
  forgottenPasswordSuccess?: boolean | undefined;
}

const cachedUser = localStorage.getItem('user');

const initialState: LoginState = {
  loginError: undefined,
  user: cachedUser ? (JSON.parse(cachedUser) as User) : undefined,
  loading: true,
  tfaQrCode: undefined,
  tfaError: undefined,
  forgottenPasswordError: undefined,
  forgottenPasswordSuccess: undefined,
};

export const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    clearUserData: (state) => {
      localStorage.removeItem('user');
      localStorage.removeItem('registerUserFormData');

      state.loginError = initialState.loginError;
      state.user = undefined;
      state.loading = initialState.loading;
    },
    resetError: (state) => {
      state.loginError = initialState.loginError;
      state.tfaError = initialState.tfaError;
      state.forgottenPasswordError = initialState.forgottenPasswordError;
      state.forgottenPasswordSuccess = initialState.forgottenPasswordSuccess;
    },
  },
  extraReducers: (builder) => {
    // fetch user
    builder.addCase(fetchUser.pending, (state) => {
      state.loginError = initialState.loginError;
      state.user = undefined;
      state.loading = initialState.loading;
    });
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    });
    builder.addCase(fetchUser.rejected, (state, action) => {
      state.loading = false;
      state.loginError = action.payload
        ? (action.payload as Error)?.message
        : action.error?.message;
    });

    // login
    builder.addCase(login.pending, (state) => {
      state.loginError = initialState.loginError;
      state.user = undefined;
      state.loading = initialState.loading;
      state.tfaQrCode = initialState.tfaQrCode;
      state.tfaError = initialState.tfaError;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.loading = false;
      state.tfaQrCode = action.payload.qrCode;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.loginError = action.payload
        ? (action.payload as Error)?.message
        : action.error?.message;
    });

    // tfa verify
    builder.addCase(tfaVerifyCode.pending, (state) => {
      state.tfaError = initialState.tfaError;
      state.user = undefined;
      state.loading = initialState.loading;
    });
    builder.addCase(tfaVerifyCode.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    });
    builder.addCase(tfaVerifyCode.rejected, (state, action) => {
      state.loading = false;
      state.tfaError = action.payload
        ? (action.payload as Error)?.message
        : action.error?.message;
    });

    builder.addCase(updateProfileData.fulfilled, (state, action) => {
      state.user = { ...state.user, ...action.meta.arg } as User;
      localStorage.setItem('user', JSON.stringify(state.user));
    });

    // forgotten-password
    builder.addCase(forgottenPassword.pending, (state) => {
      state.forgottenPasswordError = initialState.forgottenPasswordError;
      state.forgottenPasswordSuccess = initialState.forgottenPasswordSuccess;
    });
    builder.addCase(forgottenPassword.fulfilled, (state, action) => {
      state.loading = false;
      state.forgottenPasswordSuccess = action.payload;
    });
    builder.addCase(forgottenPassword.rejected, (state, action) => {
      state.loading = false;
      state.forgottenPasswordError = action.payload
        ? (action.payload as Error)?.message
        : action.error?.message;
      state.forgottenPasswordSuccess = false;
    });
  },
});

export const { clearUserData, resetError } = loginSlice.actions;

export default loginSlice.reducer;
